import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from "@angular/core";
import {WidgetComponent} from "@feature/dashboards";
import {
  DebtorAccountHistorySequence,
  getSequenceCategory,
  oneLineForEachReceiptOptions,
  separateLineForEachReceiptOptions,
  SequenceCategory
} from "@feature/_app_modules/debtor-account-history-widget/debtor-account-history-widget-types";
import {FilterEditorDialogComponent} from "@feature/common";
import { DropdownChangeEvent } from "primeng/dropdown";
import {AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from "@angular/forms";

/**
 * Provides the UI for obtaining the user specified sequence selection which
 * is one of the 5 options defined by `DebtorAccountHistorySequence`
 * This component encapsulates the logic and UI for the user to switch between
 * the two categories of item sequence defined by `SequenceCategory`.
 * This simplifies the host component because the 'api' of the component is
 * only the sequence value, the sequence category exists only for the UI at
 * this component level.
 */
@Component({
  selector: ' debtor-account-history-widget-sequence-selection',
  templateUrl: './debtor-account-history-widget-sequence-selection.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DebtorAccountHistoryWidgetSequenceSelectionComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DebtorAccountHistoryWidgetSequenceSelectionComponent
    }
  ]
})
export class DebtorAccountHistoryWidgetSequenceSelectionComponent
  extends WidgetComponent
  implements ControlValueAccessor, Validator, OnInit {

  @Input()
  filterEditor: FilterEditorDialogComponent;

  @Input()
  selectedSequence: DebtorAccountHistorySequence;

  @Output()
  selectedSequenceChange: EventEmitter<DebtorAccountHistorySequence> = new EventEmitter<DebtorAccountHistorySequence>();

  SC = SequenceCategory;
  sequenceCategory: SequenceCategory =
    SequenceCategory.SeparateLineForEachReceipt;
  readonly separateLineForEachReceiptOptions = separateLineForEachReceiptOptions;
  readonly oneLineForEachReceiptOptions = oneLineForEachReceiptOptions;

  disabled = false;
  writeValue(sequence: DebtorAccountHistorySequence): void {
    this.setSequence(sequence);
  }
  onTouched = () => { };
  registerOnTouched(fn): void { this.onTouched = fn; }
  onChange = (sequence: DebtorAccountHistorySequence) => { };
  registerOnChange(fn): void { this.onChange = fn; }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl<any, any>): ValidationErrors {
    return this.selectedSequence
      ? {}
      : { 'Invalid Sequence': this.selectedSequence };
  }
  onValidatorChanged = () => { };
  registerOnValidatorChange?(fn: () => void): void { this.onValidatorChanged = fn; }

  ngOnInit() : void {
    if (!this.selectedSequence)
      this.selectedSequence = DebtorAccountHistorySequence.SortChargesByDateWithReceiptsFollowingTheirRespectiveCharges;
    this.sequenceCategory = getSequenceCategory(this.selectedSequence);
  }

  onCategoryChanged(): void {
    this.selectedSequence = null;
    this.selectedSequenceChange.emit(null);
    this.onValidatorChanged();
  }

  onSequenceChanged($event: DropdownChangeEvent) : void {
    this.setSequence($event.value);
    this.selectedSequenceChange.emit(this.selectedSequence);
  }

  setSequence(sequence: DebtorAccountHistorySequence) : void {
    this.selectedSequence = sequence;
    this.sequenceCategory = getSequenceCategory(this.selectedSequence);
    this.onChange(this.selectedSequence);
  }

  getLabelClass(enabled: boolean) : string {
    return 'ml-2' + (enabled ? '' : ' lighter-font-weight');
  }

  getDropdownClass(enabled: boolean) : string {
    return enabled && !this.selectedSequence ? 'ng-dirty ng-invalid' : '';
  }
}
